import React from 'react';
import { motion } from 'framer-motion';
interface ColumviBackgroundProps {
  children: React.ReactNode;
  showWave?: boolean;
  animateWave?: boolean;
}
export function ColumviBackground({
  children,
  showWave = false,
  animateWave = false
}: ColumviBackgroundProps) {
  return <div className="relative w-full h-full overflow-hidden" style={{
    background: 'linear-gradient(170deg, #2D6A35 0%, #1E4D2B 40%, #163D22 100%)'
  }} data-id="element-5">
      {/* Subtle cellular dot pattern overlay */}
      <div className="absolute inset-0 opacity-[0.04]" style={{
      backgroundImage: `
            radial-gradient(circle at 20% 30%, rgba(255,255,255,0.8) 1px, transparent 1px),
            radial-gradient(circle at 60% 70%, rgba(255,255,255,0.6) 1px, transparent 1px),
            radial-gradient(circle at 80% 20%, rgba(255,255,255,0.7) 1px, transparent 1px),
            radial-gradient(circle at 40% 80%, rgba(255,255,255,0.5) 1px, transparent 1px),
            radial-gradient(circle at 10% 60%, rgba(255,255,255,0.6) 1px, transparent 1px),
            radial-gradient(circle at 90% 50%, rgba(255,255,255,0.4) 1px, transparent 1px)
          `,
      backgroundSize: '80px 80px, 120px 120px, 100px 100px, 90px 90px, 110px 110px, 70px 70px'
    }} data-id="element-6" />

      {/* Subtle organic gradient overlay */}
      <div className="absolute inset-0 opacity-[0.08]" style={{
      background: 'radial-gradient(ellipse at 30% 50%, rgba(231,111,36,0.3) 0%, transparent 60%)'
    }} data-id="element-7" />

      {/* Content */}
      <div className="relative z-10 w-full h-full flex flex-col" data-id="element-8">
        {children}
      </div>

      {/* Orange gradient wave at bottom */}
      {showWave && <div className="absolute bottom-0 left-0 right-0 z-[5]" data-id="element-9">
          <svg viewBox="0 0 400 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full" preserveAspectRatio="none" style={{
        display: 'block'
      }} data-id="element-10">
            <defs data-id="element-11">
              <linearGradient id="waveGradient1" x1="0" y1="0" x2="400" y2="0" gradientUnits="userSpaceOnUse" data-id="element-12">
                <stop offset="0%" stopColor="#E76F24" data-id="element-13" />
                <stop offset="50%" stopColor="#F09030" data-id="element-14" />
                <stop offset="100%" stopColor="#F4A261" data-id="element-15" />
              </linearGradient>
              <linearGradient id="waveGradient2" x1="0" y1="0" x2="400" y2="0" gradientUnits="userSpaceOnUse" data-id="element-16">
                <stop offset="0%" stopColor="#D4621E" data-id="element-17" />
                <stop offset="50%" stopColor="#E76F24" data-id="element-18" />
                <stop offset="100%" stopColor="#C8581A" data-id="element-19" />
              </linearGradient>
              <linearGradient id="waveGradient3" x1="0" y1="0" x2="400" y2="0" gradientUnits="userSpaceOnUse" data-id="element-20">
                <stop offset="0%" stopColor="#F4A261" data-id="element-21" />
                <stop offset="40%" stopColor="#F09030" data-id="element-22" />
                <stop offset="100%" stopColor="#E8944A" data-id="element-23" />
              </linearGradient>
            </defs>

            {/* Back wave layer - slowest, lightest */}
            <motion.path fill="url(#waveGradient3)" opacity="0.45" animate={animateWave ? {
          d: ['M0 65 C50 50, 100 40, 150 55 S250 75, 300 58 S370 42, 400 55 L400 120 L0 120 Z', 'M0 58 C50 70, 110 48, 160 60 S240 42, 290 62 S360 55, 400 48 L400 120 L0 120 Z', 'M0 70 C60 45, 120 58, 170 48 S260 68, 310 52 S380 60, 400 62 L400 120 L0 120 Z', 'M0 65 C50 50, 100 40, 150 55 S250 75, 300 58 S370 42, 400 55 L400 120 L0 120 Z']
        } : {}} transition={{
          duration: 10,
          repeat: Infinity,
          ease: 'easeInOut'
        }} d="M0 65 C50 50, 100 40, 150 55 S250 75, 300 58 S370 42, 400 55 L400 120 L0 120 Z" data-id="element-24" />

            {/* Middle wave layer - medium speed */}
            <motion.path fill="url(#waveGradient1)" opacity="0.7" animate={animateWave ? {
          d: ['M0 72 C40 55, 80 45, 120 60 S200 78, 250 62 S330 48, 370 58 L400 55 L400 120 L0 120 Z', 'M0 62 C45 72, 90 52, 130 68 S210 50, 260 70 S340 58, 380 65 L400 60 L400 120 L0 120 Z', 'M0 68 C35 48, 85 65, 125 52 S195 72, 245 55 S325 68, 375 52 L400 65 L400 120 L0 120 Z', 'M0 72 C40 55, 80 45, 120 60 S200 78, 250 62 S330 48, 370 58 L400 55 L400 120 L0 120 Z']
        } : {}} transition={{
          duration: 7,
          repeat: Infinity,
          ease: 'easeInOut'
        }} d="M0 72 C40 55, 80 45, 120 60 S200 78, 250 62 S330 48, 370 58 L400 55 L400 120 L0 120 Z" data-id="element-25" />

            {/* Front wave layer - fastest, most opaque */}
            <motion.path fill="url(#waveGradient2)" opacity="0.85" animate={animateWave ? {
          d: ['M0 82 C50 68, 100 58, 150 72 S240 88, 300 75 S370 62, 400 70 L400 120 L0 120 Z', 'M0 75 C55 85, 110 65, 160 78 S235 62, 290 80 S365 72, 400 78 L400 120 L0 120 Z', 'M0 80 C45 65, 95 78, 145 68 S230 82, 285 70 S360 80, 400 72 L400 120 L0 120 Z', 'M0 82 C50 68, 100 58, 150 72 S240 88, 300 75 S370 62, 400 70 L400 120 L0 120 Z']
        } : {}} transition={{
          duration: 5,
          repeat: Infinity,
          ease: 'easeInOut'
        }} d="M0 82 C50 68, 100 58, 150 72 S240 88, 300 75 S370 62, 400 70 L400 120 L0 120 Z" data-id="element-26" />
          </svg>
        </div>}
    </div>;
}